import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

function useSmoothWheel(
	targetRef: RefObject<HTMLDivElement | null>,
	speed: number,
	smooth: number
) {
	const pos = useRef(0);
	const moving = useRef(false);

	useEffect(() => {
		const target = targetRef?.current;
		if (!target) return;

		pos.current = target.scrollTop;

		const handleScroll = (e: WheelEvent) => {
			e.preventDefault();
			const delta = normalizeWheelDelta(e);
			pos.current += -delta * speed;
			pos.current = Math.max(
				0,
				Math.min(pos.current, target.scrollHeight - target.clientHeight)
			);

			if (!moving.current) updateScroll();
		};

		const normalizeWheelDelta = (e: WheelEvent) => {
			if (e.detail) {
				if (e.deltaY < 0) {
					return (-e.deltaY / e.detail / 40) * (e.detail > 0 ? 1 : -1);
				} else {
					return -e.detail / 3;
				}
			} else {
				return e.deltaY / 120;
			}
		};

		const updateScroll = () => {
			moving.current = true;
			const delta = (pos.current - target.scrollTop) / smooth;
			target.scrollTop += delta;

			if (Math.abs(delta) > 0.5) {
				requestFrame(updateScroll);
			} else {
				moving.current = false;
			}
		};

		const requestFrame = (() => {
			return (
				window.requestAnimationFrame ||
				function (func) {
					window.setTimeout(func, 1000 / 50);
				}
			);
		})();

		target.addEventListener('wheel', handleScroll, { passive: false });
		return () => {
			target.removeEventListener('wheel', handleScroll);
		};
	}, [speed, smooth, targetRef]);

	return targetRef;
}

export default useSmoothWheel;
